import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  RadioGroup,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useFormik } from 'formik';
import { t } from 'i18next';
import { useAddCustomMatchMutation, useEditMatchMutation } from 'src/services/tournaments';
import useToast from 'src/hooks/useToast';
import CustomFormLabel from 'src/components/forms/theme-elements/CustomFormLabel';
import CustomTextField from 'src/components/forms/theme-elements/CustomTextField';
import { useSelector } from 'src/store/Store';
import BlankCard from 'src/components/shared/BlankCard';
import { Drawer, DrawerHeader } from 'src/components/shared';
import toast from 'react-hot-toast';
import CustomSelect from 'src/components/forms/theme-elements/CustomSelect';
import * as Yup from 'yup';

interface Props {
  open: boolean;
  onClose: () => void;
  loadMatches: () => void;
}

const validationSchema = Yup.object().shape({
  tournament_team_1_id: Yup.number().required(t('TournamentScheduleTab.team1Required')),
  tournament_team_2_id: Yup.number().required(t('TournamentScheduleTab.team2Required')),
  position: Yup.string().required(t('TournamentScheduleTab.positionRequired')),
});

const CreateExtraMatch = ({ open, onClose, loadMatches }: Props) => {
  const tournamentDetails = useSelector((state) => state.tournamentReducer.selectedTournament);
  const minimalTeams = useSelector((state) => state.tournamentReducer.minimalTeams);
  const tournamentSchedule = useSelector((state) => state.tournamentReducer.tournamentSchedule);

  const [createCustomMatch] = useAddCustomMatchMutation();

  const { toastPromise } = useToast();

  const formik = useFormik({
    initialValues: {
      tournament_match_group_id: undefined,
      round: 1,
      referral_round: undefined,
      position: '',
      tournament_team_1_id: undefined,
      tournament_team_2_id: undefined,
    },
    validationSchema,
    onSubmit: (values) => {
      toastPromise({
        request: () =>
          createCustomMatch({
            tournamentId: tournamentDetails?.id,
            body: {
              ...values,
            },
          }),
        onSuccess: (result) => {
          if ('error' in result) {
            throw result.error;
          }
          loadMatches();
          onClose();
          formik.resetForm();

          return t('TournamentScheduleTab.matchCreatedSuccessfully');
        },
        onError: (err) => {
          if (err.data && err.data.errors) {
            const errorMessages = Object.values(err.data.errors).flat().join('. ');

            return errorMessages;
          }
          if (err?.data?.message) {
            return err?.data?.message;
          }

          return t('TournamentScheduleTab.matchCreationFailed');
        },
      });
    },
  });

  useEffect(() => {
    if (tournamentSchedule) {
      formik.setFieldValue(
        'tournament_match_group_id',
        Object.values(tournamentSchedule?.data)?.[0]?.tournament_match_groups?.[0]?.id,
      );
    }
  }, [tournamentSchedule]);

  const onCloseDrawer = () => {
    onClose();
    formik.resetForm();
  };

  return (
    <Drawer
      PaperProps={{
        sx: {
          width: '50%',
          padding: '20px',
        },
      }}
      className="booking-form-drawer"
      anchor="right"
      open={open}
      onClose={onCloseDrawer}
    >
      <BlankCard className="booking-form-card">
        <DrawerHeader
          title={t('TournamentScheduleTab.createExtraMatches')}
          onClose={onCloseDrawer}
        />
        <form onSubmit={formik.handleSubmit}>
          <Box
            sx={{
              height: 'calc(100vh - 100px)',
              overflow: 'auto',
              paddingBottom: '40px',
              paddingRight: 5,
              paddingLeft: 5,
            }}
            className="booking-form-content"
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <CustomFormLabel htmlFor="position">
                  {t('TournamentScheduleTab.position')}
                </CustomFormLabel>
                <CustomTextField
                  id="position"
                  fullWidth
                  {...formik.getFieldProps('position')}
                  error={formik.touched.position && Boolean(formik.errors.position)}
                  helperText={formik.touched.position && formik.errors.position}
                />
              </Grid>
              <Grid item xs={12}>
                <CustomFormLabel htmlFor="tournament_team_1_id">
                  {t('TournamentsScreen.team1')}
                </CustomFormLabel>
                <CustomSelect
                  id="tournament_team_1_id"
                  fullWidth
                  {...formik.getFieldProps('tournament_team_1_id')}
                  error={
                    formik.touched.tournament_team_1_id &&
                    Boolean(formik.errors.tournament_team_1_id)
                  }
                  helperText={
                    formik.touched.tournament_team_1_id && formik.errors.tournament_team_1_id
                  }
                >
                  {minimalTeams?.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option?.player_1?.name + ' ' + option?.player_2?.name}
                    </MenuItem>
                  ))}
                </CustomSelect>
                {formik.touched.tournament_team_1_id && formik.errors.tournament_team_1_id && (
                  <FormHelperText error>{formik.errors.tournament_team_1_id}</FormHelperText>
                )}
              </Grid>
              <Grid item xs={12}>
                <CustomFormLabel htmlFor="tournament_team_2_id">
                  {t('TournamentsScreen.team2')}
                </CustomFormLabel>
                <CustomSelect
                  id="tournament_team_2_id"
                  fullWidth
                  {...formik.getFieldProps('tournament_team_2_id')}
                  error={
                    formik.touched.tournament_team_2_id &&
                    Boolean(formik.errors.tournament_team_2_id)
                  }
                  helperText={
                    formik.touched.tournament_team_2_id && formik.errors.tournament_team_2_id
                  }
                >
                  {minimalTeams?.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option?.player_1?.name + ' ' + option?.player_2?.name}
                    </MenuItem>
                  ))}
                </CustomSelect>
                {formik.touched.tournament_team_2_id && formik.errors.tournament_team_2_id && (
                  <FormHelperText error>{formik.errors.tournament_team_2_id}</FormHelperText>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12} mt={5}>
              <Stack direction="row" spacing={2}>
                <Button variant="text" color="error" onClick={onCloseDrawer}>
                  {t('Common.cancel')}
                </Button>
                <Button type="submit" sx={{ marginLeft: 2 }} variant="contained" color={'primary'}>
                  {t('Common.submit')}
                </Button>
              </Stack>
            </Grid>
          </Box>
        </form>
      </BlankCard>
    </Drawer>
  );
};

export default CreateExtraMatch;
